import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Save,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Sparkles,
  Send
} from 'lucide-react';
import { aiService } from '../../../lib/aiService';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Slider } from '../../ui/slider';
import { Textarea } from '../../ui/textarea';
import { Progress } from '../../ui/progress';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { db } from '../../../lib/db';
import { formatDate } from '../../../lib/dateUtils';
import {
  calculateManagerScore,
  calculateWeightedScore,
  formatStatus,
  getScoreColor,
  getScoreLabel,
  getStatusBadgeColor,
  calculateProgress
} from '../../../lib/calculations';
import { v4 as uuidv4 } from 'uuid';
import { useAuthStore } from '../../../store/authStore';
import AIAnalysisSummary from '../../shared/AIAnalysisSummary';

const scoreLabels = {
  0: 'Weak',
  1: 'Needs Improvement',
  2: 'Below Expectations',
  3: 'Meets Expectations',
  4: 'Exceeds Expectations',
  5: 'Outstanding'
};

const initialGoalState = { title: '', description: '', dueDate: '', priority: 'medium', status: 'in_progress' };
const initialFutureGoalState = { title: '', description: '', dueDate: '', priority: 'medium', status: 'not_started' };

export default function SupervisorAppraisalReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [appraisal, setAppraisal] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('performance');
  const [isSaving, setIsSaving] = useState(false);
  const [isEnhancingAll, setIsEnhancingAll] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showSendToCeoModal, setShowSendToCeoModal] = useState(false);
  const [supervisorRecommendation, setSupervisorRecommendation] = useState({
    type: '',
    notes: '',
    salaryHikePercentage: '',
    promotionRole: ''
  });
  const [newGoal, setNewGoal] = useState(initialGoalState);
  const [newFutureGoal, setNewFutureGoal] = useState(initialFutureGoalState);
  const [supervisorGoals, setSupervisorGoals] = useState([]);
  const [importSelection, setImportSelection] = useState([]);
  const [importTarget, setImportTarget] = useState('current');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showFutureGoalModal, setShowFutureGoalModal] = useState(false);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const idKey = isNaN(Number(id)) ? id : Number(id);
      const data = await db.appraisals.get(idKey) || await db.appraisals.get(String(id));
      if (!data) {
        toast.error('Appraisal not found');
        navigate(-1);
        return;
      }
      setAppraisal(data);
      const emp = await db.users.get(data.userId);
      setEmployee(emp);
      if (user?.id) {
        const goals = await db.goals.where('userId').equals(user.id).toArray();
        setSupervisorGoals(goals);
      }
    } catch (error) {
      console.error('Error loading appraisal', error);
      toast.error('Failed to load appraisal');
    }
  };

  const recomputeProgress = (draft) => {
    const performanceAreasComplete = draft.performanceAreas?.every(area => area.managerScore !== undefined || area.completedByManager);
    const goalsComplete = (draft.goals?.current?.length || 0) > 0 || (draft.goals?.future?.length || 0) > 0;
    const commentsComplete = Boolean(draft.comments?.manager || draft.comments?.employee);
    return {
      performanceAreasComplete,
      goalsComplete,
      commentsComplete,
      overallPercent: calculateProgress(draft)
    };
  };

  const handleScoreChange = (index, value) => {
    setAppraisal(prev => {
      const updated = { ...prev };
      updated.performanceAreas = [...(prev?.performanceAreas || [])];
      updated.performanceAreas[index] = { ...updated.performanceAreas[index], managerScore: value[0], completedByManager: true };
      return updated;
    });
  };

  const handleCommentChange = (index, value) => {
    setAppraisal(prev => {
      const updated = { ...prev };
      updated.performanceAreas = [...(prev?.performanceAreas || [])];
      updated.performanceAreas[index] = { ...updated.performanceAreas[index], managerComment: value };
      return updated;
    });
  };

  const handleManagerOverallComment = (value) => {
    setAppraisal(prev => ({
      ...prev,
      comments: { ...(prev?.comments || {}), manager: value }
    }));
  };

  const handleEnhanceAll = async () => {
    setIsEnhancingAll(true);
    const toastId = toast.loading("Enhancing all comments...");
    
    try {
      const updated = { ...appraisal };
      let enhancedCount = 0;

      // Enhance Performance Areas
      const areaPromises = updated.performanceAreas.map(async (area) => {
        if (area.managerComment && area.managerComment.trim()) {
           try {
             const corrected = await aiService.correctText(area.managerComment);
             enhancedCount++;
             return { ...area, managerComment: corrected };
           } catch (e) {
             console.error("Failed to enhance area comment:", e);
             return area;
           }
        }
        return area;
      });

      const improvedAreas = await Promise.all(areaPromises);
      updated.performanceAreas = improvedAreas;

      // Enhance Overall Comment
      if (updated.comments?.manager && updated.comments.manager.trim()) {
        try {
          const correctedOverall = await aiService.correctText(updated.comments.manager);
          updated.comments.manager = correctedOverall;
          enhancedCount++;
        } catch (e) {
           console.error("Failed to enhance overall comment:", e);
        }
      }

      setAppraisal(updated);
      toast.success(`Enhanced ${enhancedCount} comments!`, { id: toastId });
    } catch (error) {
       console.error(error);
       toast.error("Failed to enhance comments", { id: toastId });
    } finally {
       setIsEnhancingAll(false);
    }
  };

  const handleImproveComment = async (index, text) => {
    if (!text || !text.trim()) {
      toast.error("Please write a draft comment first.");
      return;
    }
    
    const toastId = toast.loading("Enhancing comment with AI...");
    try {
      const improved = await aiService.correctText(text);
      handleCommentChange(index, improved);
      toast.success("Comment enhanced!", { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error("Failed to enhance comment", { id: toastId });
    }
  };

  const handleImproveOverallComment = async (text) => {
    if (!text || !text.trim()) {
      toast.error("Please write a draft comment first.");
      return;
    }

    const toastId = toast.loading("Enhancing overall assessment with AI...");
    try {
      const improved = await aiService.correctText(text);
      handleManagerOverallComment(improved);
      toast.success("Assessment enhanced!", { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error("Failed to enhance assessment", { id: toastId });
    }
  };

  const managerProgress = appraisal?.performanceAreas?.length
    ? Math.round(
        (appraisal.performanceAreas.filter(area => area.managerScore !== undefined).length /
          appraisal.performanceAreas.length) * 100
      )
    : 0;

  const needsManagerComment = (area) => {
    if (area.managerScore === undefined) return false;
    return area.managerScore <= 1 || area.managerScore >= 5;
  };

  const canFinalize = appraisal?.performanceAreas?.length > 0 &&
    appraisal.performanceAreas.every(area => {
      if (area.managerScore === undefined) return false;
      if (needsManagerComment(area)) {
        return Boolean(area.managerComment && area.managerComment.trim().length > 0);
      }
      return true;
    });

  const persistAppraisal = async (updates, successMessage) => {
    try {
      setIsSaving(true);
      const merged = { ...appraisal, ...updates, updatedAt: Date.now() };
      merged.progress = { ...(merged.progress || {}), ...recomputeProgress(merged) };
      await db.appraisals.put(merged);
      setAppraisal(merged);
      if (successMessage) {
        toast.success(successMessage);
      }
    } catch (error) {
      console.error('Error updating appraisal', error);
      toast.error('Unable to update appraisal');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    await persistAppraisal({ status: appraisal.status === 'employee_completed' ? 'manager_review' : appraisal.status }, 'Draft saved');
  };

  const handleFinalize = async () => {
    const managerTotal = calculateManagerScore(appraisal.performanceAreas);
    const finalWeightedScore = calculateWeightedScore(appraisal.performanceAreas);

    await persistAppraisal(
      {
        status: 'finalized',
        finalizedAt: Date.now(),
        managerSubmittedAt: Date.now(),
        scores: {
          ...(appraisal.scores || {}),
          managerTotal,
          finalWeightedScore
        },
        progress: {
          ...(appraisal.progress || {}),
          ...recomputeProgress({
            ...appraisal,
            scores: {
              ...(appraisal.scores || {}),
              managerTotal,
              finalWeightedScore
            }
          })
        }
      },
      'Appraisal finalized'
    );
    setShowFinalizeModal(false);
  };

  const handleSendToCeo = async () => {
    if (!supervisorRecommendation.type) {
      toast.error('Please select a recommendation type');
      return;
    }

    const managerTotal = calculateManagerScore(appraisal.performanceAreas);
    const finalWeightedScore = calculateWeightedScore(appraisal.performanceAreas);

    await persistAppraisal(
      {
        status: 'sent_to_ceo',
        sentToCeoAt: Date.now(),
        supervisorId: user?.id,
        supervisorRecommendation: {
          type: supervisorRecommendation.type,
          notes: supervisorRecommendation.notes,
          salaryHikePercentage: supervisorRecommendation.type === 'salary_hike' || supervisorRecommendation.type === 'promotion' 
            ? parseFloat(supervisorRecommendation.salaryHikePercentage) || 0 
            : null,
          promotionRole: supervisorRecommendation.type === 'promotion' 
            ? supervisorRecommendation.promotionRole 
            : null,
          recommendedBy: user?.id,
          recommendedAt: Date.now()
        },
        scores: {
          ...(appraisal.scores || {}),
          managerTotal,
          finalWeightedScore
        },
        progress: {
          ...(appraisal.progress || {}),
          ...recomputeProgress({
            ...appraisal,
            scores: {
              ...(appraisal.scores || {}),
              managerTotal,
              finalWeightedScore
            }
          })
        }
      },
      'Appraisal sent to CEO for review with recommendation'
    );
    setShowSendToCeoModal(false);
    setSupervisorRecommendation({ type: '', notes: '', salaryHikePercentage: '', promotionRole: '' });
    navigate('/supervisor/appraisals');
  };

  const handleNewGoalChange = (field, value) => {
    setNewGoal(prev => ({ ...prev, [field]: value }));
  };

  const handleNewFutureGoalChange = (field, value) => {
    setNewFutureGoal(prev => ({ ...prev, [field]: value }));
  };

  const syncGoalsToStore = async (draft) => {
    const existing = await db.goals.where('appraisalId').equals(draft.id).toArray();
    const currentGoals = draft.goals?.current || [];
    const futureGoals = draft.goals?.future || [];
    const allGoals = [...currentGoals, ...futureGoals];
    const futureIds = new Set(futureGoals.map(g => g.id));
    const currentIds = allGoals.map(g => g.id);

    for (const goal of allGoals) {
      const isFuture = futureIds.has(goal.id);
      await db.goals.put({
        id: goal.id,
        userId: draft.userId,
        appraisalId: draft.id,
        title: goal.title,
        description: goal.description || '',
        status: goal.status || 'in_progress',
        priority: goal.priority || 'medium',
        type: goal.type || (isFuture ? 'future' : 'current'),
        dueDate: goal.dueDate,
        completedDate: goal.completedDate || null,
        employeeComment: goal.employeeComment || '',
        managerComment: goal.managerComment || '',
        rolloverToNext: goal.rolloverToNext || false,
        addedBy: goal.addedBy || 'supervisor',
        createdAt: goal.createdAt || Date.now(),
        updatedAt: Date.now()
      });
    }

    for (const goal of existing) {
      if (!currentIds.includes(goal.id)) {
        await db.goals.delete(goal.id);
      }
    }

    if (user?.id) {
      const goals = await db.goals.where('userId').equals(user.id).toArray();
      setSupervisorGoals(goals);
    }
  };

  const addGoal = async () => {
    if (!newGoal.title.trim() || !newGoal.dueDate) return;
    const goal = {
      id: uuidv4(),
      title: newGoal.title.trim(),
      description: newGoal.description.trim(),
      status: newGoal.status,
      priority: newGoal.priority,
      dueDate: new Date(newGoal.dueDate).getTime(),
      addedBy: 'supervisor'
    };
    const updated = { ...appraisal };
    updated.goals = { ...updated.goals, current: [...(updated.goals?.current || []), goal] };
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };
    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await syncGoalsToStore(updated);
      setAppraisal(updated);
      setNewGoal(initialGoalState);
      setShowGoalModal(false);
    } catch (error) {
      console.error('Error adding goal:', error);
      toast.error('Unable to add goal');
    } finally {
      setIsSaving(false);
    }
  };

  const addFutureGoal = async () => {
    if (!newFutureGoal.title.trim() || !newFutureGoal.dueDate) return;
    const goal = {
      id: uuidv4(),
      title: newFutureGoal.title.trim(),
      description: newFutureGoal.description.trim(),
      status: newFutureGoal.status,
      priority: newFutureGoal.priority,
      dueDate: new Date(newFutureGoal.dueDate).getTime(),
      addedBy: 'supervisor'
    };
    const updated = { ...appraisal };
    updated.goals = { ...updated.goals, future: [...(updated.goals?.future || []), goal] };
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };
    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await syncGoalsToStore(updated);
      setAppraisal(updated);
      setNewFutureGoal(initialFutureGoalState);
      setShowFutureGoalModal(false);
    } catch (error) {
      console.error('Error adding future goal:', error);
      toast.error('Unable to add future goal');
    } finally {
      setIsSaving(false);
    }
  };

  const removeGoal = async (goalId) => {
    const updated = { ...appraisal };
    updated.goals = {
      ...updated.goals,
      current: (updated.goals?.current || []).filter(g => g.id !== goalId)
    };
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };
    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await db.goals.delete(goalId);
      await syncGoalsToStore(updated);
      setAppraisal(updated);
    } catch (error) {
      console.error('Error removing goal:', error);
      toast.error('Unable to remove goal');
    } finally {
      setIsSaving(false);
    }
  };

  const removeFutureGoal = async (goalId) => {
    const updated = { ...appraisal };
    updated.goals = {
      ...updated.goals,
      future: (updated.goals?.future || []).filter(g => g.id !== goalId)
    };
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };
    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await syncGoalsToStore(updated);
      setAppraisal(updated);
    } catch (error) {
      console.error('Error removing future goal:', error);
      toast.error('Unable to remove goal');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleImportSelection = (goalId) => {
    setImportSelection(prev => prev.includes(goalId) ? prev.filter(id => id !== goalId) : [...prev, goalId]);
  };

  const handleImportGoals = async () => {
    if (!appraisal || importSelection.length === 0) return;
    const toImport = supervisorGoals.filter(g => importSelection.includes(g.id));
    const targetList = importTarget === 'future' ? (appraisal.goals?.future || []) : (appraisal.goals?.current || []);
    const existingIds = targetList.map(g => g.id);
    const mapped = toImport
      .filter(g => !existingIds.includes(g.id))
      .map(g => ({
        id: g.id,
        title: g.title,
        description: g.description || '',
        status: g.status || 'in_progress',
        priority: g.priority || 'medium',
        type: g.type || 'current',
        dueDate: g.dueDate,
        createdAt: g.createdAt,
        completedDate: g.completedDate,
        employeeComment: g.employeeComment,
        managerComment: g.managerComment,
        rolloverToNext: g.rolloverToNext,
        addedBy: g.addedBy || 'supervisor'
      }));

    if (mapped.length === 0) {
      setShowImportModal(false);
      setImportSelection([]);
      return;
    }

    const updated = { ...appraisal };
    if (importTarget === 'future') {
      updated.goals = { ...updated.goals, future: [...(updated.goals?.future || []), ...mapped] };
    } else {
      updated.goals = { ...updated.goals, current: [...(updated.goals?.current || []), ...mapped] };
    }
    updated.progress = { ...updated.progress, ...recomputeProgress(updated) };

    try {
      setIsSaving(true);
      await db.appraisals.put(updated);
      await syncGoalsToStore(updated);
      setAppraisal(updated);
      setShowImportModal(false);
      setImportSelection([]);
      setImportTarget('current');
    } catch (error) {
      console.error('Error importing goals:', error);
      toast.error('Unable to import goals');
    } finally {
      setIsSaving(false);
    }
  };

  if (!appraisal) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const isReadOnly = appraisal.status === 'finalized';
  const currentEmployeeGoals = (appraisal.goals?.current || []).filter(g => (g.addedBy || 'employee') !== 'supervisor');
  const currentSupervisorGoals = (appraisal.goals?.current || []).filter(g => (g.addedBy || 'employee') === 'supervisor');
  const futureEmployeeGoals = (appraisal.goals?.future || []).filter(g => (g.addedBy || 'employee') !== 'supervisor');
  const futureSupervisorGoals = (appraisal.goals?.future || []).filter(g => (g.addedBy || 'employee') === 'supervisor');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{appraisal.title}</h1>
            <Badge className={getStatusBadgeColor(appraisal.status)}>{formatStatus(appraisal.status)}</Badge>
          </div>
          <p className="text-gray-500 mt-1">Due: {formatDate(appraisal.dueDate)}</p>
          {employee && (
            <p className="text-sm text-gray-500 mt-1">
              Employee: {employee.profile?.firstName} {employee.profile?.lastName}
            </p>
          )}
        </div>
        {!isReadOnly && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSaveDraft} disabled={isSaving}>
              <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button
              variant="outline"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              disabled={!canFinalize || isSaving}
              onClick={() => setShowSendToCeoModal(true)}
            >
              <Send className="w-4 h-4 mr-2" /> Send to CEO
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700 text-white"
              disabled={!canFinalize || isSaving}
              onClick={() => setShowFinalizeModal(true)}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Finalize Review
            </Button>
          </div>
        )}
      </div>



      <div className="flex items-center gap-2">
        <AIAnalysisSummary appraisal={appraisal} />
        {!isReadOnly && (
          <Button
            onClick={handleEnhanceAll}
            disabled={isEnhancingAll}
            className={`
              bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 
              text-white shadow-md hover:shadow-lg transition-all duration-300
              ${isEnhancingAll ? 'opacity-80' : ''}
            `}
          >
            <Sparkles className={`w-4 h-4 mr-2 ${isEnhancingAll ? 'animate-spin' : ''}`} />
            {isEnhancingAll ? 'Enhancing All...' : 'Enhance All with AI'}
          </Button>
        )}
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Manager Review Progress</p>
              <div className="flex items-center gap-3 mt-2">
                <Progress value={managerProgress} className="h-2 w-44" />
                <span className="text-lg font-semibold text-gray-900">{managerProgress}%</span>
              </div>
            </div>
            {appraisal.scores?.employeeSelfTotal && (
              <div className="text-right">
                <p className="text-sm font-medium text-gray-500">Employee Self Score</p>
                <p className={`text-2xl font-bold ${getScoreColor(appraisal.scores.employeeSelfTotal)}`}>
                  {appraisal.scores.employeeSelfTotal.toFixed(1)}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="performance" className="data-[state=active]:bg-white">Performance Areas</TabsTrigger>
          <TabsTrigger value="goals" className="data-[state=active]:bg-white">Goals</TabsTrigger>
          <TabsTrigger value="comments" className="data-[state=active]:bg-white">Comments</TabsTrigger>
          {appraisal.status === 'finalized' && (
            <TabsTrigger value="summary" className="data-[state=active]:bg-white">Summary</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="performance" className="space-y-4 mt-6">
          {appraisal.performanceAreas.map((area, index) => {
            const requiresComment = needsManagerComment(area);
            const hasManagerResponse = area.managerScore !== undefined || (area.managerComment && area.managerComment.trim().length > 0);
            return (
              <Card key={area.competencyId} className="border-0 shadow-sm">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{area.competencyName}</h3>
                      <p className="text-sm text-gray-500">Weight: {area.weight}%</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {area.completedByManager && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                      {area.managerScore !== undefined && (
                        <Badge className={`text-lg px-3 py-1 ${getScoreColor(area.managerScore)}`}>
                          {Number(area.managerScore).toFixed(1)}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 mb-1">Employee Response</p>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Badge className={`text-xs ${getScoreColor(area.employeeScore)}`}>
                        {area.employeeScore !== undefined ? Number(area.employeeScore).toFixed(1) : 'N/A'}
                      </Badge>
                      <span className="text-gray-500">{area.employeeComment || 'No comment provided'}</span>
                    </div>
                  </div>

                  {hasManagerResponse && (
                    <div className="p-3 bg-indigo-50 rounded-lg">
                      <p className="text-xs font-medium text-gray-600 mb-1">Manager Response</p>
                      <div className="flex items-center gap-2 text-sm text-gray-800">
                        <Badge className={`text-xs ${getScoreColor(area.managerScore)}`}>
                          {area.managerScore !== undefined ? Number(area.managerScore).toFixed(1) : 'N/A'}
                        </Badge>
                        <span className="text-gray-600">{area.managerComment || 'No manager comment provided'}</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="flex justify-between text-xs text-gray-400 mb-2">
                      {Object.entries(scoreLabels).map(([score, label]) => (
                        <span key={score} className="text-center w-16">{label}</span>
                      ))}
                    </div>
                    <Slider
                      value={[area.managerScore ?? area.employeeScore ?? 3]}
                      onValueChange={(value) => handleScoreChange(index, value)}
                      max={5}
                      min={0}
                      step={0.1}
                      disabled={isReadOnly}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm font-medium mt-2">
                      {[0, 1, 2, 3, 4, 5].map(n => (
                        <span key={n} className={Math.round(area.managerScore ?? area.employeeScore ?? 3) === n ? 'text-violet-600' : 'text-gray-300'}>
                          {n}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">
                        Manager Comments {requiresComment && <span className="text-rose-500">*</span>}
                      </label>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                          onClick={() => handleImproveComment(index, area.managerComment)}
                          disabled={isReadOnly}
                        >
                          <Sparkles className="w-3 h-3 mr-1" /> Enhance with AI
                        </Button>
                      </div>
                    </div>
                    <Textarea
                      placeholder={requiresComment ? 'Comment required for this score' : 'Add your feedback'}
                      value={area.managerComment || ''}
                      onChange={(e) => handleCommentChange(index, e.target.value)}
                      disabled={isReadOnly}
                      className={requiresComment && !area.managerComment ? 'border-rose-300' : ''}
                    />
                    {requiresComment && !area.managerComment && (
                      <p className="text-xs text-rose-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Comment required for scores ≤1 or ≥5
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="comments" className="space-y-6 mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Overall Feedback</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Employee Comment</p>
                <div className="bg-gray-50 p-4 rounded-lg text-gray-700 min-h-16">
                  {appraisal.comments?.employee || 'No employee comment provided'}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">Manager Comment</p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                    onClick={() => handleImproveOverallComment(appraisal.comments?.manager)}
                    disabled={isReadOnly}
                  >
                    <Sparkles className="w-3 h-3 mr-1" /> Enhance with AI
                  </Button>
                </div>
                <Textarea
                  placeholder="Share your overall assessment..."
                  value={appraisal.comments?.manager || ''}
                  onChange={(e) => handleManagerOverallComment(e.target.value)}
                  disabled={isReadOnly}
                  className="min-h-32"
                />
              </div>
              {/* CEO Review Section - only show if CEO has reviewed */}
              {appraisal.comments?.ceo && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm font-medium text-emerald-700 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    CEO Review
                  </p>
                  <div className="bg-emerald-50 p-4 rounded-lg text-gray-700 min-h-16 border border-emerald-100">
                    {appraisal.comments.ceo}
                  </div>
                  {appraisal.ceoApprovedAt && (
                    <p className="text-xs text-gray-500 mt-2">
                      Approved by CEO on {new Date(appraisal.ceoApprovedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="goals" className="space-y-6 mt-6">
          {!isReadOnly && (
            <>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex gap-2">
                  <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => setShowGoalModal(true)} disabled={isSaving}>
                    Add Goal
                  </Button>
                  <Button className="bg-teal-600/90 hover:bg-teal-700 text-white" onClick={() => setShowFutureGoalModal(true)} disabled={isSaving}>
                    Add Future Goal
                  </Button>
                  <Button variant="outline" onClick={() => setShowImportModal(true)} disabled={isSaving || supervisorGoals.length === 0}>
                    Import from My Goals
                  </Button>
                </div>
                <div className="text-sm text-gray-500">Add or pull goals into this review.</div>
              </div>
            </>
          )}

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Current Goals</CardTitle>
            </CardHeader>
            <CardContent>
              {(currentEmployeeGoals.length + currentSupervisorGoals.length) > 0 ? (
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3">Employee Goals</p>
                    {currentEmployeeGoals.length > 0 ? (
                      <div className="space-y-4">
                        {currentEmployeeGoals.map(goal => (
                          <div key={goal.id} className="p-4 border border-gray-100 rounded-xl">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{goal.title}</h4>
                                <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                                <p className="text-xs text-gray-400 mt-2">Due: {formatDate(goal.dueDate)}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={`${
                                  goal.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                  goal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {goal.status?.replace('_', ' ')}
                                </Badge>
                                <Badge className={`${
                                  goal.priority === 'high' ? 'bg-rose-100 text-rose-700' :
                                  goal.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {goal.priority}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No employee-submitted goals</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3">Supervisor Goals</p>
                    {currentSupervisorGoals.length > 0 ? (
                      <div className="space-y-4">
                        {currentSupervisorGoals.map(goal => (
                          <div key={goal.id} className="p-4 border border-gray-100 rounded-xl">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{goal.title}</h4>
                                <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                                <p className="text-xs text-gray-400 mt-2">Due: {formatDate(goal.dueDate)}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={`${
                                  goal.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                  goal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {goal.status?.replace('_', ' ')}
                                </Badge>
                                <Badge className={`${
                                  goal.priority === 'high' ? 'bg-rose-100 text-rose-700' :
                                  goal.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {goal.priority}
                                </Badge>
                                {!isReadOnly && (
                                  <Button variant="ghost" size="sm" onClick={() => removeGoal(goal.id)}>
                                    Remove
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No supervisor-added goals</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No current goals</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Future Goals</CardTitle>
            </CardHeader>
            <CardContent>
              {(futureEmployeeGoals.length + futureSupervisorGoals.length) > 0 ? (
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3">Employee Future Goals</p>
                    {futureEmployeeGoals.length > 0 ? (
                      <div className="space-y-4">
                        {futureEmployeeGoals.map(goal => (
                          <div key={goal.id} className="p-4 border border-gray-100 rounded-xl">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{goal.title}</h4>
                                <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                                <p className="text-xs text-gray-400 mt-2">Target: {formatDate(goal.dueDate)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No employee future goals</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3">Supervisor Future Goals</p>
                    {futureSupervisorGoals.length > 0 ? (
                      <div className="space-y-4">
                        {futureSupervisorGoals.map(goal => (
                          <div key={goal.id} className="p-4 border border-gray-100 rounded-xl">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{goal.title}</h4>
                                <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                                <p className="text-xs text-gray-400 mt-2">Target: {formatDate(goal.dueDate)}</p>
                              </div>
                              {!isReadOnly && (
                                <Button variant="ghost" size="sm" onClick={() => removeFutureGoal(goal.id)}>
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No supervisor future goals</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No future goals defined</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {appraisal.status === 'finalized' && (
          <TabsContent value="summary" className="space-y-6 mt-6">
            <Card className="border-0 shadow-sm bg-gradient-to-br from-violet-50 to-indigo-50">
              <CardContent className="pt-6 text-center space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Final Weighted Score</p>
                  <p className={`text-5xl font-bold ${getScoreColor(appraisal.scores?.finalWeightedScore)}`}>
                    {appraisal.scores?.finalWeightedScore?.toFixed(2) || 'N/A'}
                  </p>
                  <Badge className="mt-3 bg-white">{getScoreLabel(appraisal.scores?.finalWeightedScore)}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white/70 rounded-lg">
                    <p className="text-sm text-gray-500">Employee Self Score</p>
                    <p className="text-xl font-semibold text-gray-900">{appraisal.scores?.employeeSelfTotal?.toFixed(2) || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-white/70 rounded-lg">
                    <p className="text-sm text-gray-500">Manager Score</p>
                    <p className="text-xl font-semibold text-gray-900">{appraisal.scores?.managerTotal?.toFixed(2) || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <GoalModal
        title="Add a Goal"
        isOpen={showGoalModal}
        onClose={() => setShowGoalModal(false)}
        goal={newGoal}
        onChange={handleNewGoalChange}
        onSave={addGoal}
        isSaving={isSaving}
        dueLabel="Due Date"
      />

      <GoalModal
        title="Add a Future Goal"
        isOpen={showFutureGoalModal}
        onClose={() => setShowFutureGoalModal(false)}
        goal={newFutureGoal}
        onChange={handleNewFutureGoalChange}
        onSave={addFutureGoal}
        isSaving={isSaving}
        dueLabel="Target Date"
        descriptionPlaceholder="What will they pursue next period?"
      />

      <Dialog open={showFinalizeModal} onOpenChange={setShowFinalizeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalize Appraisal</DialogTitle>
            <DialogDescription>
              Finalizing will lock manager inputs and mark this review as complete.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinalizeModal(false)}>Cancel</Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700 text-white"
              onClick={handleFinalize}
              disabled={isSaving || !canFinalize}
            >
              {isSaving ? 'Finalizing...' : 'Finalize Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSendToCeoModal} onOpenChange={setShowSendToCeoModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Send to CEO for Review</DialogTitle>
            <DialogDescription>
              Provide your recommendation for this employee before sending to the CEO for final review.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Recommendation Type */}
            <div className="space-y-2">
              <Label htmlFor="recommendationType" className="text-sm font-medium">
                Recommendation Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={supervisorRecommendation.type}
                onValueChange={(value) => setSupervisorRecommendation(prev => ({ 
                  ...prev, 
                  type: value,
                  salaryHikePercentage: '',
                  promotionRole: ''
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select recommendation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="promotion">Recommend for Promotion</SelectItem>
                  <SelectItem value="salary_hike">Recommend for Salary Hike</SelectItem>
                  <SelectItem value="retain">Retain in Current Role</SelectItem>
                  <SelectItem value="development">Needs Development</SelectItem>
                  <SelectItem value="pip">Performance Improvement Plan (PIP)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Salary Hike Percentage - Show for promotion and salary_hike */}
            {(supervisorRecommendation.type === 'promotion' || supervisorRecommendation.type === 'salary_hike') && (
              <div className="space-y-2">
                <Label htmlFor="salaryHike" className="text-sm font-medium">
                  Recommended Salary Hike (%)
                </Label>
                <Input
                  id="salaryHike"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="e.g., 15"
                  value={supervisorRecommendation.salaryHikePercentage}
                  onChange={(e) => setSupervisorRecommendation(prev => ({ 
                    ...prev, 
                    salaryHikePercentage: e.target.value 
                  }))}
                />
              </div>
            )}

            {/* Promotion Role - Show for promotion */}
            {supervisorRecommendation.type === 'promotion' && (
              <div className="space-y-2">
                <Label htmlFor="promotionRole" className="text-sm font-medium">
                  Recommended New Role/Title
                </Label>
                <Input
                  id="promotionRole"
                  placeholder="e.g., Senior Software Engineer"
                  value={supervisorRecommendation.promotionRole}
                  onChange={(e) => setSupervisorRecommendation(prev => ({ 
                    ...prev, 
                    promotionRole: e.target.value 
                  }))}
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-medium">
                Justification / Notes
              </Label>
              <Textarea
                id="notes"
                placeholder="Provide justification for your recommendation..."
                rows={3}
                value={supervisorRecommendation.notes}
                onChange={(e) => setSupervisorRecommendation(prev => ({ 
                  ...prev, 
                  notes: e.target.value 
                }))}
              />
            </div>

            {/* Info Note */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> You will not be able to make further changes until the CEO returns it or provides final approval.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowSendToCeoModal(false);
              setSupervisorRecommendation({ type: '', notes: '', salaryHikePercentage: '', promotionRole: '' });
            }}>Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleSendToCeo}
              disabled={isSaving || !canFinalize || !supervisorRecommendation.type}
            >
              <Send className="w-4 h-4 mr-2" />
              {isSaving ? 'Sending...' : 'Send with Recommendation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import from My Goals</DialogTitle>
            <DialogDescription>Select existing goals to pull into this appraisal.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 text-sm text-gray-700 mb-3">
            <span>Import to:</span>
            <select
              className="border rounded-md px-2 py-1 text-sm"
              value={importTarget}
              onChange={(e) => setImportTarget(e.target.value)}
            >
              <option value="current">Current Goals</option>
              <option value="future">Future Goals</option>
            </select>
          </div>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {supervisorGoals.length === 0 && (
              <p className="text-sm text-gray-500">No goals found for you.</p>
            )}
            {supervisorGoals.map(goal => {
              const alreadyInAppraisal = importTarget === 'future'
                ? (appraisal?.goals?.future || []).some(g => g.id === goal.id)
                : (appraisal?.goals?.current || []).some(g => g.id === goal.id);
              return (
                <div key={goal.id} className={`flex items-start gap-3 p-3 border border-gray-100 rounded-lg ${alreadyInAppraisal ? 'bg-gray-50' : ''}`}>
                  <Checkbox
                    checked={importSelection.includes(goal.id)}
                    disabled={alreadyInAppraisal}
                    onCheckedChange={() => toggleImportSelection(goal.id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{goal.title}</h4>
                      <Badge className={`${
                        goal.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        goal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {goal.status?.replace('_', ' ')}
                      </Badge>
                      <Badge className={`${
                        goal.priority === 'high' ? 'bg-rose-100 text-rose-700' :
                        goal.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {goal.priority}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{goal.description || 'No description provided.'}</p>
                    <p className="text-xs text-gray-400 mt-1">Due: {formatDate(goal.dueDate)}</p>
                    {alreadyInAppraisal && <p className="text-xs text-emerald-600 mt-1">Already included in this appraisal</p>}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleImportGoals} disabled={importSelection.length === 0 || isSaving}>
              Import Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GoalModal({ title, isOpen, onClose, goal, onChange, onSave, isSaving, dueLabel = 'Due Date', descriptionPlaceholder = 'What is the goal?' }) {
  const canSave = goal.title.trim() && goal.dueDate;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Define the goal details before saving.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Title</label>
            <Input
              placeholder="Goal title"
              value={goal.title}
              onChange={(e) => onChange('title', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{dueLabel}</label>
            <Input
              type="date"
              value={goal.dueDate}
              onChange={(e) => onChange('dueDate', e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Description</label>
          <Textarea
            placeholder={descriptionPlaceholder}
            value={goal.description}
            onChange={(e) => onChange('description', e.target.value)}
            className="min-h-24"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Priority</label>
            <select
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={goal.priority}
              onChange={(e) => onChange('priority', e.target.value)}
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={goal.status}
              onChange={(e) => onChange('status', e.target.value)}
            >
              <option value="in_progress">In Progress</option>
              <option value="not_started">Not Started</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={onSave} disabled={!canSave || isSaving}>
            {isSaving ? 'Saving...' : 'Save Goal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
